/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import React from "react";
import { When } from "react-if";
import { useNavigate } from "react-router-dom";
import {
  FlexContainer,
  MultipleSelect,
  SelectButton,
  Sidebar,
  Text,
  theme,
} from "@nordcloud/gnui";
import {
  useAttachResourceMutation,
  useGetAppsWithEnvsQuery,
} from "~/generated/graphql";
import { LoaderWrap } from "~/components";
import { showError, showSuccess } from "~/services/toast";
import { getFirstItem, inflect, isNotNil } from "~/tools";
import { CostMappingAlgorithm } from "../settings/CostSplittingRulesPage/types";
import { SelectAppEnvFormData } from "./components/AppEnvSelector/types";
import { CostSplittingRule, costSplittingRules } from "./components/constants";
import {
  useCostSplittingRules,
  useResourceCostSplitRule,
} from "./components/hooks";
import { ApplicationOption } from "./components/types";
import {
  MultipleEnvironmentForm,
  MultipleEnvironmentFormData,
} from "./EstateDetailsPage/components/MultipleEnvironmentForm";
import { SingleEnvironmentForm } from "./EstateDetailsPage/components/SingleEnvironmentForm";
import { isGreaterThan } from "./helpers";
import { Resource } from "./types";

type Props = {
  resources: Resource[];
  refresh?: () => void;
  isOpen: boolean;
  onClose: () => void;
  allowMultipleAssignment?: boolean;
  saveSelections?: boolean;
};

export function AttachToEnv({
  resources,
  refresh,
  isOpen,
  onClose,
  allowMultipleAssignment = false,
  saveSelections,
}: Props) {
  const navigate = useNavigate();

  const [updateResource, { loading }] = useAttachResourceMutation();

  const { costSplitRules } = useCostSplittingRules(
    // Do not remove optional chaining, it will cause runtime error
    getFirstItem(resources)?.nid
  );

  const { attachRules, loading: loadingRules } = useResourceCostSplitRule();

  const onUpdate = async (data: SelectAppEnvFormData) => {
    let successCount = 0;

    for (const { nid } of resources) {
      try {
        await updateResource({
          variables: { environmentNid: data.environmentId, resourceNid: nid },
        });
        successCount += 1;
      } catch {
        showError("Something went wrong");
      }
    }

    onClose();
    refresh?.();

    showSuccess(
      `We've allocated ${successCount} ${inflect("resource")(
        successCount
      )}. Click here to see the environment.`,
      false,
      () =>
        navigate(`/applications/${data.applicationId}/${data.environmentId}`)
    );
  };

  const onMultipleEnvUpdate = async (data: MultipleEnvironmentFormData) => {
    const envSettings =
      data.selectedAlgorithm === CostMappingAlgorithm.Manual
        ? data.envCosts.filter((item) => item.splitValue)
        : data.envCosts;

    await attachRules({
      resources,
      algorithm: data.selectedAlgorithm,
      settings: envSettings,
    });

    onClose();
    refresh?.();
  };

  const [activeRule, setActiveRule] = React.useState(
    allowMultipleAssignment &&
      isGreaterThan(costSplitRules?.resourceCostSplitRules.count, 0)
      ? CostSplittingRule.multipleEnv
      : CostSplittingRule.singleEnv
  );

  const { data: apps, loading: loadingApps } = useGetAppsWithEnvsQuery({
    notifyOnNetworkStatusChange: true,
    skip: !isOpen,
  });

  const filteredApplications: ApplicationOption[] = (apps?.applications ?? [])
    .filter(isNotNil)
    .map((application) => ({
      id: application.id,
      name: application.name,
      nid: application.nid,
      environments: (
        application.environments?.map((env) => ({
          id: env?.id ?? "",
          name: env?.name ?? "",
          nid: env?.nid ?? "",
        })) ?? []
      ).filter(isNotNil),
    }));

  const isFirefoxBrowser = navigator.userAgent.includes("Firefox");

  return (
    <>
      <Sidebar
        // Css is temporary fix for crashing estate page
        // Issue occurs only on chrome (v98) on mac for browser width 2560px
        // Test scenario: load estate records list with 50 items
        // Please check if it's already fixed in chrome and if so - remove this line :)
        // Update: Fix from above was causing issues in Firefox with nested sidebars
        css={{ willChange: isFirefoxBrowser ? "auto" : "transform" }}
        title="Attach Environment"
        isOpen={isOpen}
        onClick={onClose}
      >
        <LoaderWrap inContent loading={loadingApps || loadingRules || loading}>
          <Text align="left">Attach resource to</Text>
          <FlexContainer
            justifyContent="center"
            alignItems="center"
            marginTop={theme.spacing.spacing02}
            marginBottom={theme.spacing.spacing04}
          >
            <MultipleSelect>
              {costSplittingRules.map((rule) => (
                <SelectButton
                  key={rule.name}
                  name={rule.name}
                  value={rule.value}
                  labelText={rule.labelText}
                  isActive={activeRule === rule.value}
                  onClick={() => setActiveRule(rule.value)}
                />
              ))}
            </MultipleSelect>
          </FlexContainer>
          <When condition={activeRule === CostSplittingRule.singleEnv}>
            <SingleEnvironmentForm
              applications={filteredApplications}
              onSubmit={onUpdate}
            />
          </When>
          <When condition={activeRule === CostSplittingRule.multipleEnv}>
            <MultipleEnvironmentForm
              applications={filteredApplications}
              costSplitRules={costSplitRules?.resourceCostSplitRules.result[0]}
              saveSelections={saveSelections}
              onSubmit={onMultipleEnvUpdate}
            />
          </When>
        </LoaderWrap>
      </Sidebar>
    </>
  );
}
